// The toDom tag-wrap table below is copied from dojo/dom-construct —
// Copyright (c) The JS Foundation, BSD-3-Clause. See ../../LICENSE.
// mds-dojo — dojo/dom-construct. create/place/empty/destroy/toDom.
// place() supports the same position vocabulary dojo/dijit rely on:
// numeric index, "first", "last", "before", "after", "replace", "only".

import domAttr from './dom-attr';

// Tag-wrap map (from dojo/dom-construct): elements that can't be parsed as a bare
// innerHTML on a <div> (a <tr> etc. is silently dropped) must be wrapped in their
// required ancestor context, then unwrapped. Without this, a widget whose template
// root is <tr>/<td>/<option>/<li> would come up empty.
const TAG_WRAP: Record<string, string[]> = {
  option: ['select'], tbody: ['table'], thead: ['table'], tfoot: ['table'],
  tr: ['table', 'tbody'], td: ['table', 'tbody', 'tr'], th: ['table', 'thead', 'tr'],
  legend: ['fieldset'], caption: ['table'], colgroup: ['table'], col: ['table', 'colgroup'], li: ['ul'],
};
const WRAP: Record<string, { pre: string; post: string; depth: number }> = {};
for (const tag of Object.keys(TAG_WRAP)) {
  const chain = TAG_WRAP[tag];
  const pre = tag === 'option' ? '<select multiple="multiple">' : '<' + chain.join('><') + '>';
  const post = '</' + chain.slice().reverse().join('></') + '>';
  WRAP[tag] = { pre, post, depth: chain.length };
}

export function toDom(frag: string, doc?: Document): Node {
  doc = doc || document;
  const s = String(frag).replace(/^\s+/, '');
  const m = s.match(/^<\s*([\w:]+)/);
  const tag = m ? m[1].toLowerCase() : '';
  let container: any = doc.createElement('div');
  const w = WRAP[tag];
  if (w) {
    container.innerHTML = w.pre + s + w.post;
    let d = w.depth;
    while (d--) { container = container.firstChild; }
  } else {
    container.innerHTML = s;
  }
  if (container.childNodes.length === 1) {
    return container.removeChild(container.firstChild as Node);
  }
  // Multiple top-level nodes → return a fragment (dijit templates should be single-root).
  const df = doc.createDocumentFragment();
  while (container.firstChild) { df.appendChild(container.firstChild); }
  return df;
}

export function place(node: Node | string, refNode: Node | string, position?: string | number): Node {
  const ref = typeof refNode === 'string' ? (document.getElementById(refNode) as Node) : refNode;
  // dojo parity: a STRING `node` is either inline HTML (starts with '<' → toDom,
  // which yields a fragment when the markup has >1 top-level node) or an element
  // id (→ byId). Without this the raw string reached appendChild → "Argument 1 is
  // not an object" (e.g. ExpandableSection.setHeading places heading.innerHTML).
  const n: Node = typeof node === 'string'
    ? (/^\s*</.test(node)
        ? toDom(node, (ref && (ref as Node).ownerDocument) || document)
        : (document.getElementById(node) as Node))
    : node;
  const parent = ref.parentNode;

  if (typeof position === 'number') {
    const cn = (ref as Element).childNodes;
    if (!cn.length || cn.length <= position) {
      ref.appendChild(n);
    } else {
      ref.insertBefore(n, cn[position < 0 ? 0 : position]);
    }
    return n;
  }

  switch (position) {
    case 'before':
      parent!.insertBefore(n, ref);
      break;
    case 'after':
      parent!.insertBefore(n, ref.nextSibling);
      break;
    case 'replace':
      parent!.replaceChild(n, ref);
      break;
    case 'only':
      while ((ref as Element).firstChild) { (ref as Element).removeChild((ref as Element).firstChild as Node); }
      ref.appendChild(n);
      break;
    case 'first':
      ref.insertBefore(n, (ref as Element).firstChild);
      break;
    case 'last':
    default:
      ref.appendChild(n);
      break;
  }
  return n;
}

export function create(tag: string | Element, attrs?: any, refNode?: Node | string, position?: string | number): HTMLElement {
  const doc = document;
  const node = typeof tag === 'string' ? doc.createElement(tag) : tag;
  if (attrs) { domAttr.set(node, attrs); }
  if (refNode) { place(node, refNode, position); }
  return node as HTMLElement;
}

export function empty(node: Element | string): void {
  const n = typeof node === 'string' ? document.getElementById(node) : node;
  if (!n) { return; }
  while (n.firstChild) { n.removeChild(n.firstChild); }
}

export function destroy(node: Node | string): void {
  const n = typeof node === 'string' ? document.getElementById(node) : node;
  if (n && n.parentNode) { n.parentNode.removeChild(n); }
}

const domConstruct = { toDom, place, create, empty, destroy };
export default domConstruct;
